import { addMonths, format, getDate, isAfter, parse } from 'date-fns';
import { CreditCardData } from 'design-system';
import { ANNUAL, OTHER } from 'src/constants/paymentQuantityOptions';
import { creditCard } from 'src/constants/paymentTypes';
import ACHData from '../../../../models/helperTypes/ACHData';
import { PaymentDataFields } from '../../../../store/emissionPolicy/slice/emissionPolicySlice';

import FormShape from './FormShape';

/**
 * Returns true if the provided date has a valid format
 *
 * @param sourceDate - The date to check
 */
export function validDateString(sourceDate: string): string {
  if (sourceDate === '') {
    return format(new Date(), 'dd/MM/yyyy');
  }
  return sourceDate;
}

export function shouldOpenUpsellingModal(
  createdAt: string,
  defaultValue: boolean,
): boolean {
  const limitDate = parse('09/04/2024', 'dd/MM/yyyy', new Date());
  const quotationDate = new Date(createdAt);
  return isAfter(quotationDate, limitDate) && defaultValue;
}

export function getPaymentDayNumber(date: string): string {
  return date === '31' ? '30' : date;
}

export function flattenData(
  fields: PaymentDataFields,
  creditCardData: CreditCardData,
  achData: ACHData,
  paymentIssuerSource: string,
): FormShape {
  const date = getDate(new Date());
  const paymentDayNumber = getPaymentDayNumber(date.toString());
  return {
    type: creditCard,
    paymentQuantity: fields.paymentQuantity,
    paymentQuantityType: fields.paymentQuantityType,
    policyStartDate: validDateString(fields.policyStartDate),
    paymentDayNumber,
    paymentAmount: fields.paymentAmount,
    ...achData,
    ...creditCardData,
    paymentIssuerSource,
    collectionAuthorization: false,
  };
}

export function parseToPaymentDataFields(data: FormShape): PaymentDataFields {
  return {
    type: data.type,
    paymentQuantity: data.paymentQuantity,
    paymentQuantityType: data.paymentQuantityType,
    policyStartDate: data.policyStartDate,
    paymentDayNumber: data.paymentDayNumber,
    paymentAmount: data.paymentAmount,
  };
}

export function parseToCreditCardFields(data: FormShape): CreditCardData {
  return {
    cardName: data.cardName,
    cardNumber: data.cardNumber,
    cardType: data.cardType,
    expirationDate: data.expirationDate,
    securityCode: data.securityCode,
    bankEntityCode: data.bankEntityCode,
    cardAddress: data.cardAddress,
  };
}

export function parseToACHDataFields(data: FormShape): ACHData {
  return {
    accountBankId: data.accountBankId,
    accountBankEntityName: data.accountBankEntityName,
    accountNumber: data.accountNumber,
    accountOwnerName: data.accountOwnerName,
    accountType: data.accountType,
    isValidAccountNumber: data.isValidAccountNumber,
  };
}

export function makeDates(startDate: string): {
  today: Date;
  twoMonthsAhead: Date;
} {
  let today = new Date();
  if (startDate !== '') {
    today = parse(startDate, 'dd/MM/yyyy', new Date());
  }
  const twoMonthsAhead = addMonths(today, 2);
  return { today, twoMonthsAhead };
}

/** This returns the text in cards for new cars and with autopx discount
 *
 * @param hasFreeMonth - Validates if it is a new car with autopx discount
 * @param type - if is anual or other (mensual)
 */
export function getFreeMonthText(
  hasFreeMonth: boolean,
  type: string,
  profileID: number,
): string {
  if (hasFreeMonth) {
    if (profileID === 28) {
      return 'Beneficio incluido (2 meses GRATIS)';
    }

    if (type === ANNUAL) {
      return `Beneficio incluido (Un mes GRATIS)`;
    }

    if (type === OTHER) {
      return `Beneficio incluido (1er mes GRATIS)`;
    }

    return '';
  }
  return '';
}
